/**
 * Tooltip — singleton component for hover/long-press tooltips.
 * Desktop: mouseenter/mouseleave on trigger.
 * Mobile: 300ms long-press shows tooltip, suppresses tap.
 */

const LONG_PRESS_MS = 300;
const GAP = 8;
const VIEWPORT_MARGIN = 8;

class TooltipManager {
  private el: HTMLElement | null = null;
  private activeTrigger: HTMLElement | null = null;
  private longPressTimer: number | null = null;
  private hoverTimer: number | null = null;
  private suppressClick = false;

  initialize(): void {
    this.el = document.getElementById('tooltip');
    if (!this.el) return;

    // Dismiss on any touch outside tooltip/trigger
    document.addEventListener('touchstart', (e) => {
      if (!this.activeTrigger) return;
      const target = e.target as Node;
      if (this.el?.contains(target) || this.activeTrigger.contains(target)) return;
      this.hide();
    }, { passive: true });

    // Dismiss on scroll
    document.addEventListener('scroll', () => this.hide(), { passive: true, capture: true });
  }

  /**
   * Attach hover + long-press listeners to a trigger element.
   * contentFn is called lazily each time the tooltip is shown.
   */
  attach(trigger: HTMLElement, contentFn: () => string, options?: { delay?: number }): void {
    const delay = options?.delay ?? 0;

    // Desktop: hover (with optional delay)
    trigger.addEventListener('mouseenter', () => {
      if (delay > 0) {
        this.hoverTimer = window.setTimeout(() => {
          this.hoverTimer = null;
          this.show(trigger, contentFn());
        }, delay);
      } else {
        this.show(trigger, contentFn());
      }
    });
    trigger.addEventListener('mouseleave', () => {
      if (this.hoverTimer !== null) {
        clearTimeout(this.hoverTimer);
        this.hoverTimer = null;
      }
      this.hide();
    });
    // Hide tooltip on click (so it doesn't interfere with click handlers)
    trigger.addEventListener('click', () => {
      if (this.hoverTimer !== null) {
        clearTimeout(this.hoverTimer);
        this.hoverTimer = null;
      }
      this.hide();
    });
    // Hide tooltip when dragging starts
    trigger.addEventListener('dragstart', () => {
      this.hide();
    });

    // Mobile: long-press
    trigger.addEventListener('touchstart', (e) => {
      this.suppressClick = false;
      this.longPressTimer = window.setTimeout(() => {
        this.longPressTimer = null;
        this.suppressClick = true;
        this.show(trigger, contentFn());
      }, LONG_PRESS_MS);
    }, { passive: true });

    trigger.addEventListener('touchend', (e) => {
      if (this.longPressTimer !== null) {
        // Released before threshold — cancel, let normal tap through
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
      if (this.suppressClick) {
        e.preventDefault();
        this.suppressClick = false;
      }
    });

    trigger.addEventListener('touchmove', () => {
      if (this.longPressTimer !== null) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
    }, { passive: true });
  }

  show(trigger: HTMLElement, html: string): void {
    if (!this.el) return;

    this.activeTrigger = trigger;
    this.el.innerHTML = html;
    this.el.classList.add('visible');

    // Position after content is rendered
    requestAnimationFrame(() => this.position(trigger));
  }

  hide(): void {
    if (!this.el) return;
    this.el.classList.remove('visible');
    this.activeTrigger = null;
  }

  private position(trigger: HTMLElement): void {
    if (!this.el) return;

    const triggerRect = trigger.getBoundingClientRect();
    const tipRect = this.el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Prefer above
    let top = triggerRect.top - tipRect.height - GAP;
    if (top < VIEWPORT_MARGIN) {
      // Flip below
      top = triggerRect.bottom + GAP;
    }
    // Clamp vertically
    top = Math.min(top, vh - tipRect.height - VIEWPORT_MARGIN);
    top = Math.max(top, VIEWPORT_MARGIN);

    // Center horizontally on trigger
    let left = triggerRect.left + triggerRect.width / 2 - tipRect.width / 2;
    // Clamp horizontally
    left = Math.min(left, vw - tipRect.width - VIEWPORT_MARGIN);
    left = Math.max(left, VIEWPORT_MARGIN);

    this.el.style.top = `${top}px`;
    this.el.style.left = `${left}px`;
  }
}

export const Tooltip = new TooltipManager();
